import {
  Component,
  HostListener,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { UserErrorMessages } from 'src/app/constants/ErrorMessages';
import { UserLabelConstants } from 'src/app/constants/LabelConstants';
import { ApiUrlHelper } from 'src/app/common/api-url-helper';
import { CommonService } from 'src/app/services/common/common.service';
import { DirtyComponent } from 'src/app/component/dirty/dirty.component';
import { city, country, role, state } from 'src/app/models/commonModel';
import { StorageKey, StorageService } from 'src/app/services/storage/storage.service';

@Component({
    selector: 'app-add-edit-user-dialog',
    templateUrl: './add-edit-user-dialog.component.html',
    styleUrls: ['./add-edit-user-dialog.component.scss'],
    standalone: false
})
export class AddEditUserDialogComponent implements OnInit {

  // Common Properties
  UserId: any;
  AddUserForm: FormGroup;
  isEditing: boolean;
  imageSrc: string;
  countryList: any = [];
  countryId: any;
  RoleList: any = [];
  EditedUserDetails: any;
  UserImage: any;
  submitted: boolean = false;
  isZipValid: boolean = false;
  ErrorMessages: any;
  IsPreviewTrue: boolean = false;
  LabelConstants: any;
  protected AllRoleList: role[] = this.RoleList;
  public RoleCtrl: FormControl = new FormControl();
  public FilteredRole: ReplaySubject<any> = new ReplaySubject();
  protected AllCountrylist: country[] = this.countryList;
  public CountryCtrl: FormControl = new FormControl();
  protected FilteredCountry: ReplaySubject<any> = new ReplaySubject();
  loginId: any;

  @ViewChild('singleSelect', { static: true })
  singleSelect!: MatSelect;
  protected _onDestroy = new Subject();

  ngOnDestroy() {
    this._onDestroy.next(1);
    this._onDestroy.complete();
  }

  constructor(
    private dialogRef: MatDialogRef<AddEditUserDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private api: ApiUrlHelper,
    private commonService: CommonService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private storageService: StorageService
  ) {
    this.isEditing = data.IsEdit;
    this.UserId = data.UserId;
    this.IsPreviewTrue = data.ShowPreview;
    if (this.isEditing || this.UserId > 0 || this.IsPreviewTrue) {
      Number.parseInt(this.UserId);
      this.fetchUserDetails();
    }
  }

  ngOnInit(): void {
    this.getUserRoleList();
    this.getCountryList();
    this.AddUserForm = this.fb.group({
      UserImage: [''],
      FirstName: ['', Validators.required],
      LastName: ['', Validators.required],
      Role: ['', Validators.required],
      Email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^(?=.{1,30}@)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
          ),
        ],
      ],
      PhoneNumber: ['', [Validators.required, Validators.minLength(10)]],
      Address: ['', Validators.required],
      Country: ['', Validators.required],
      State: ['', Validators.required],
      City: ['', Validators.required],
      PostalCode: ['', [Validators.required, Validators.minLength(2)]],
    });
    this.ErrorMessages = UserErrorMessages;
    this.LabelConstants = UserLabelConstants;

    this.FilteredRole.next(this.AllRoleList.slice());
    this.RoleCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.FilterRole();
      });

    this.FilteredCountry.next(this.AllCountrylist.slice());
    this.CountryCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.FilterCountry();
    });
    this.loginId = this.storageService.getValue(StorageKey.userId);
  }

  cancel() {
    if (this.AddUserForm.dirty) {
      const dialogRef = this.dialog.open(DirtyComponent, {
        width: '500px',
        data: {
          action: 'User',
        },
        disableClose: true,
        autoFocus: false
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  public canDeactivated(event: BeforeUnloadEvent): void {
    if (this.AddUserForm.dirty) {
      event.preventDefault();
      event.returnValue = false;
    }
  }

  getDialogTitle() {
    if (this.IsPreviewTrue) {
      return 'User Preview';
    }
    return this.isEditing ? 'Edit User' : 'Add User';
  }

  get GetUserFormControls() {
    return this.AddUserForm.controls;
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const fileSizeInBytes = file.size;
      const maxSizeInBytes = 10 * 1024 * 1024; // 10MB

      if (fileSizeInBytes > maxSizeInBytes) {
        this.toastr.error(
          'Selected file exceeds the maximum allowed size (10MB).'
        );
        return;
      }
      this.UserImage = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imageSrc = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  isZipCodeValid(event: any) {
    let isValid = this.commonService.isZipCodeValid(event);
    if (isValid) {
      this.isZipValid = true;
    }
    else {
      this.isZipValid = false;
    }
  }

  submitUserForm() {
    this.submitted = true;
    this.isZipCodeValid(this.AddUserForm.value.PostalCode);
    if (this.AddUserForm.valid && this.isZipValid) {
      this.AddUserForm.get('Email')?.enable();
      this.spinner.show();
      this.AddUserForm.get('Email')?.enable();
      let api = this.api.apiUrl.User.AddUpdateUser;
      const formData = new FormData();
      formData.append('UserId', this.UserId || '0');
      formData.append('RoleId', this.AddUserForm.value.Role);
      formData.append('Email', this.AddUserForm.value.Email?.trim());
      formData.append('Firstname', this.AddUserForm.value.FirstName?.trim());
      formData.append('Lastname', this.AddUserForm.value.LastName?.trim());
      formData.append('Images', this.UserImage);
      formData.append('MobileNumber', this.AddUserForm.value.PhoneNumber?.trim());
      formData.append('UserAddress', this.AddUserForm.value.Address?.trim());
      formData.append('CountryId', this.AddUserForm.value.Country);
      formData.append('State', this.AddUserForm.value.State?.trim());
      formData.append('City', this.AddUserForm.value.City?.trim());
      formData.append('PostalCode', this.AddUserForm.value.PostalCode?.trim());
      this.commonService.doPost(api, formData).pipe().subscribe({
        next: (response) => {
          if (response.success) {
            this.toastr.success(response.message);
            if (this.UserId == this.loginId) {
              this.commonService.setUserImage(this.imageSrc);
            }
            this.spinner.hide();
            this.dialogRef.close(true);
          } else {
            this.toastr.error(response.message);
            this.spinner.hide();
          }
        },
        error: (err) => {
          this.spinner.hide();
          console.log(err);
        }
      });
    }
  }

  //handle image error
  handleImageError() {
    this.imageSrc = '../../../assets/img/default-user-profile.svg';
  }

  getUserRoleList() {
    let api = this.api.apiUrl.Role.GetRole;
    this.FilteredRole.next([]);
    this.AllRoleList = [];
    this.commonService.doGet(api).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.AllRoleList = response.data.map((item) => ({
            id: item.roleManagementId,
            value: item.roleName,
          }));
          this.FilterRole();
        }
      },
      error: (err) => {
        this.spinner.hide();
        console.log(err);
      }
    });
  }

  getCountryList() {
    let api = this.api.apiUrl.Country.GetCountryList;
    this.FilteredCountry.next([]);
    this.AllCountrylist = [];
    this.commonService.doGet(api).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.AllCountrylist = response.data.map((item) => ({
            id: item.countryId,
            value: item.countryname,
          }));
          this.FilterCountry();
        }
      },
      error: (err) => {
        this.spinner.hide();
        console.log(err);
      }
    });
  }

  // If User Editing Then Patch The Form Value
  fetchUserDetails() {
    this.spinner.show();
    let api = this.api.apiUrl.User.GetUserById.replace('{Id}', this.UserId);
    this.commonService.doGet(api).pipe().subscribe({
      next: (response) => {
        this.EditedUserDetails = response.data;
        this.imageSrc = this.EditedUserDetails.images;
        this.AddUserForm.patchValue({
          Images: this.EditedUserDetails.imageName,
          FirstName: this.EditedUserDetails.firstname,
          LastName: this.EditedUserDetails.lastname,
          Role: this.EditedUserDetails.roleId,
          Email: this.EditedUserDetails.email,
          PhoneNumber: this.EditedUserDetails.mobileNumber,
          Address: this.EditedUserDetails.userAddress,
          Country: this.EditedUserDetails.countryId,
          State: this.EditedUserDetails.state,
          City: this.EditedUserDetails.city,
          PostalCode: this.EditedUserDetails.postalCode,
        });
        this.AddUserForm.get('Email')?.disable();
        this.spinner.hide();
        if (this.IsPreviewTrue) {
          this.AddUserForm.disable();
        }
      },
      error: (err) => {
        this.spinner.hide();
        console.log(err);
      }
    });
  }
  protected FilterRole() {
    if (!this.AllRoleList) {
      return;
    }
    let search = this.RoleCtrl.value;
    if (!search) {
      this.FilteredRole.next(this.AllRoleList.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.FilteredRole.next(
      this.AllRoleList.filter(
        (data) => data.value.toLowerCase().indexOf(search) > -1
      )
    );
  }

  protected FilterCountry() {
    if (!this.AllCountrylist) {
      return;
    }
    let search = this.CountryCtrl.value;
    if (!search) {
      this.FilteredCountry.next(this.AllCountrylist.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.FilteredCountry.next(
      this.AllCountrylist.filter(
        (data) => data.value.toLowerCase().indexOf(search) > -1
      )
    );
  }
}